
import React, { useState, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import AdminUsers from './AdminUsers';
import SpiritualContent from './SpiritualContent';
import RecurrenceSelector from './RecurrenceSelector';

const AdminDashboard = () => {
  const [currentView, setCurrentView] = useState('events'); // 'events', 'users', 'content'
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [viewingInstances, setViewingInstances] = useState(null); // Para ver instancias de un evento recurrente
  const [instances, setInstances] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    eventDate: '',
    eventTime: '',
    location: '',
    meetingLink: '',
    eventType: 'rosary',
    isPresential: false,
    isVirtual: false,
    isActive: true,
    // Campos de recurrencia
    isRecurring: false,
    recurrenceType: null,
    recurrenceDays: [],
    recurrenceEndDate: null
  });
  const [formLoading, setFormLoading] = useState(false);

  const { admin, logout, authenticatedFetch } = useAdminAuth();

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch('/api/admin/events');
      const data = await response.json();
      
      if (data.success) {
        setEvents(data.events);
        setError('');
      } else {
        setError('Error al cargar eventos');
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  useEffect(() => {
    if (currentView === 'events') {
      fetchEvents();
    }
  }, [currentView, fetchEvents]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    return timeString.slice(0, 5);
  };

  const handleCreateNew = () => {
    setEditingEvent(null);
    setFormData({
      title: '',
      description: '',
      eventDate: '',
      eventTime: '',
      location: '',
      meetingLink: '',
      eventType: 'rosary',
      isPresential: false,
      isVirtual: false,
      isActive: true,
      isRecurring: false,
      recurrenceType: null,
      recurrenceDays: [],
      recurrenceEndDate: null
    });
    setShowForm(true);
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description,
      eventDate: new Date(event.event_date).toISOString().split('T')[0],
      eventTime: event.event_time.slice(0, 5),
      location: event.location || '',
      meetingLink: event.meeting_link || '',
      eventType: event.event_type,
      isPresential: event.is_presential,
      isVirtual: event.is_virtual,
      isActive: event.is_active,
      isRecurring: event.is_recurring || false,
      recurrenceType: event.recurrence_type || null,
      recurrenceDays: event.recurrence_days ? JSON.parse(event.recurrence_days) : [],
      recurrenceEndDate: event.recurrence_end_date || null
    });
    setShowForm(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      const url = editingEvent 
        ? `/api/admin/events/${editingEvent.id}`
        : '/api/admin/events';
      
      const method = editingEvent ? 'PUT' : 'POST';

      const response = await authenticatedFetch(url, {
        method: method,
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        setShowForm(false);
        await fetchEvents();
        alert(editingEvent ? 'Evento actualizado exitosamente' : 'Evento creado exitosamente');
      } else {
        alert('Error: ' + data.error);
      }
    } catch (error) {
      console.error('Error saving event:', error);
      alert('Error de conexión');
    } finally {
      setFormLoading(false);
    }
  };

  const handleRecurrenceChange = (recurrenceData) => {
    setFormData({
      ...formData,
      ...recurrenceData
    });
  };

  const handleViewInstances = async (event) => {
    try {
      setViewingInstances(event);
      setLoading(true);
      
      const response = await authenticatedFetch(`/api/admin/events/${event.id}/instances`);
      const data = await response.json();
      
      if (data.success) {
        setInstances(data.instances);
      } else {
        alert('Error al cargar instancias: ' + data.error);
      }
    } catch (error) {
      console.error('Error fetching instances:', error);
      alert('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateInstances = async (eventId) => {
    if (!window.confirm('¿Regenerar instancias futuras? Esto creará nuevas instancias para las próximas 8 semanas.')) {
      return;
    }

    try {
      const response = await authenticatedFetch(`/api/admin/events/${eventId}/regenerate-instances`, {
        method: 'POST',
        body: JSON.stringify({ weeksAhead: 8 })
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert(`${data.instancesGenerated} instancias generadas`);
        // Recargar instancias
        const viewEvent = events.find(e => e.id === eventId);
        if (viewEvent) {
          handleViewInstances(viewEvent);
        }
      } else {
        alert('Error: ' + data.error);
      }
    } catch (error) {
      console.error('Error regenerating instances:', error);
      alert('Error de conexión');
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const toggleEventStatus = async (eventId) => {
    try {
      const response = await authenticatedFetch(`/api/admin/events/${eventId}/toggle`, {
        method: 'PATCH'
      });
      
      const data = await response.json();
      
      if (data.success) {
        await fetchEvents();
      } else {
        alert('Error al cambiar estado del evento');
      }
    } catch (error) {
      console.error('Error toggling event:', error);
      alert('Error de conexión');
    }
  };

  const deleteEvent = async (eventId, eventTitle) => {
    if (!window.confirm(`¿Estás seguro de eliminar el evento "${eventTitle}"?`)) {
      return;
    }

    try {
      const response = await authenticatedFetch(`/api/admin/events/${eventId}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.success) {
        await fetchEvents();
      } else {
        alert('Error al eliminar evento');
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Error de conexión');
    }
  };

  if (loading && currentView === 'events') {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: '#f8f9fa'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #e2e8f0',
          borderTop: '4px solid #667eea',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '16px'
        }}></div>
        <p style={{
          color: '#718096',
          fontSize: '1.1em',
          margin: '0'
        }}>
          Cargando panel administrativo...
        </p>
        <style>
          {`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}
        </style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa' }}>
      {/* Header */}
      <header style={{
        background: 'white',
        borderBottom: '1px solid #e2e8f0',
        padding: '20px 0',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h1 style={{
              color: '#4a5568',
              margin: '0 0 4px 0',
              fontSize: '1.8em',
              fontWeight: '600'
            }}>
              Panel Administrativo
            </h1>
            <p style={{
              color: '#718096',
              margin: '0',
              fontSize: '0.95em'
            }}>
              {admin?.name} - {admin?.email}
            </p>
          </div>
          <button 
            onClick={logout}
            style={{
              background: '#e53e3e',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500',
              transition: 'background 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.background = '#c53030'}
            onMouseLeave={(e) => e.target.style.background = '#e53e3e'}
          >
            Cerrar Sesión
          </button>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div style={{
        background: 'white',
        borderBottom: '1px solid #e2e8f0',
        padding: '0 20px'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          gap: '0'
        }}>
          <button
            onClick={() => setCurrentView('events')}
            style={{
              padding: '15px 25px',
              border: 'none',
              background: 'transparent',
              borderBottom: currentView === 'events' ? '3px solid #667eea' : '3px solid transparent',
              color: currentView === 'events' ? '#667eea' : '#718096',
              fontWeight: currentView === 'events' ? '600' : '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            Gestión de Eventos
          </button>
          
          {admin?.role === 'super_admin' && (
            <button
              onClick={() => setCurrentView('users')}
              style={{
                padding: '15px 25px',
                border: 'none',
                background: 'transparent',
                borderBottom: currentView === 'users' ? '3px solid #667eea' : '3px solid transparent',
                color: currentView === 'users' ? '#667eea' : '#718096',
                fontWeight: currentView === 'users' ? '600' : '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              Gestión de Usuarios
            </button>
          )}

          <button
            onClick={() => setCurrentView('content')}
            style={{
              padding: '15px 25px',
              border: 'none',
              background: 'transparent',
              borderBottom: currentView === 'content' ? '3px solid #667eea' : '3px solid transparent',
              color: currentView === 'content' ? '#667eea' : '#718096',
              fontWeight: currentView === 'content' ? '600' : '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            Contenido Espiritual
          </button>
        </div>
      </div>

      {/* Content Area */}
      {currentView === 'users' ? (
        <AdminUsers />
      ) : currentView === 'content' ? (
        <SpiritualContent />
      ) : (
        <main style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '30px 20px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '30px'
          }}>
            <h2 style={{
              color: '#4a5568',
              margin: '0',
              fontSize: '1.5em',
              fontWeight: '600'
            }}>
              Eventos Programados ({events.length})
            </h2>
            <button 
              onClick={handleCreateNew}
              style={{
                background: '#48bb78',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#38a169';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#48bb78';
                e.target.style.transform = 'none';
              }}
            >
              + Nuevo Evento
            </button>
          </div>

          {error && (
            <div style={{
              background: '#fed7d7',
              border: '1px solid #feb2b2',
              color: '#c53030',
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              {error}
            </div>
          )}

          {/* Modal de formulario de eventos */}
          {showForm && (
            <div style={{
              position: 'fixed',
              top: '0',
              left: '0',
              right: '0',
              bottom: '0',
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: '1000',
              padding: '20px'
            }}>
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '30px',
                maxWidth: '500px',
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto'
              }}>
                <h3 style={{
                  margin: '0 0 20px 0',
                  color: '#4a5568',
                  fontSize: '1.4em'
                }}>
                  {editingEvent ? 'Editar Evento' : 'Crear Nuevo Evento'}
                </h3>

                <div>
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontWeight: '600',
                      color: '#4a5568'
                    }}>
                      Título *
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleFormChange}
                      required
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        fontSize: '1em'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontWeight: '600',
                      color: '#4a5568'
                    }}>
                      Descripción *
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleFormChange}
                      required
                      rows="3"
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        fontSize: '1em',
                        resize: 'vertical'
                      }}
                    />
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '15px',
                    marginBottom: '20px'
                  }}>
                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontWeight: '600',
                        color: '#4a5568'
                      }}>
                        Fecha *
                      </label>
                      <input
                        type="date"
                        name="eventDate"
                        value={formData.eventDate}
                        onChange={handleFormChange}
                        required
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                          fontSize: '1em'
                        }}
                      />
                    </div>
                    
                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontWeight: '600',
                        color: '#4a5568'
                      }}>
                        Hora *
                      </label>
                      <input
                        type="time"
                        name="eventTime"
                        value={formData.eventTime}
                        onChange={handleFormChange}
                        required
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                          fontSize: '1em'
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontWeight: '600',
                      color: '#4a5568'
                    }}>
                      Tipo de evento
                    </label>
                    <select
                      name="eventType"
                      value={formData.eventType}
                      onChange={handleFormChange}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        fontSize: '1em'
                      }}
                    >
                      <option value="rosary">Rosario</option>
                      <option value="mass">Misa</option>
                      <option value="prayer">Oración</option>
                      <option value="community">Comunidad</option>
                    </select>
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontWeight: '600',
                      color: '#4a5568'
                    }}>
                      Ubicación (opcional)
                    </label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleFormChange}
                      placeholder="Ej: Parroquia de la Santísima Trinidad"
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        fontSize: '1em'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontWeight: '600',
                      color: '#4a5568'
                    }}>
                      Enlace Google Meet (opcional)
                    </label>
                    <input
                      type="url"
                      name="meetingLink"
                      value={formData.meetingLink}
                      onChange={handleFormChange}
                      placeholder="https://meet.google.com/..."
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        fontSize: '1em'
                      }}
                    />
                  </div>

                  <div style={{
                    display: 'flex',
                    gap: '20px',
                    marginBottom: '30px'
                  }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        name="isPresential"
                        checked={formData.isPresential}
                        onChange={handleFormChange}
                      />
                      <span>Presencial</span>
                    </label>
                    
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        name="isVirtual"
                        checked={formData.isVirtual}
                        onChange={handleFormChange}
                      />
                      <span>Virtual</span>
                    </label>

                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleFormChange}
                      />
                      <span>Activo</span>
                    </label>
                  </div>

                  {/* Selector de Recurrencia */}
                  <RecurrenceSelector
                    isRecurring={formData.isRecurring}
                    recurrenceType={formData.recurrenceType}
                    recurrenceDays={formData.recurrenceDays}
                    recurrenceEndDate={formData.recurrenceEndDate}
                    onChange={handleRecurrenceChange}
                  />

                  <div style={{
                    display: 'flex',
                    gap: '10px',
                    justifyContent: 'flex-end'
                  }}>
                    <button
                      type="button"
                      onClick={() => setShowForm(false)}
                      style={{
                        padding: '10px 20px',
                        border: '1px solid #e2e8f0',
                        background: 'white',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleFormSubmit}
                      disabled={formLoading}
                      style={{
                        padding: '10px 20px',
                        background: formLoading ? '#a0aec0' : '#48bb78',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: formLoading ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {formLoading ? 'Guardando...' : (editingEvent ? 'Actualizar' : 'Crear')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {events.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              background: 'white',
              borderRadius: '12px',
              border: '2px dashed #e2e8f0'
            }}>
              <p style={{
                color: '#718096',
                fontSize: '1.1em',
                marginBottom: '20px'
              }}>
                No hay eventos programados
              </p>
              <button 
                onClick={handleCreateNew}
                style={{
                  background: '#667eea',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Crear primer evento
              </button>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
              gap: '20px'
            }}>
              {events.map((event) => (
                <div 
                  key={event.id} 
                  style={{
                    background: 'white',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    overflow: 'hidden',
                    transition: 'all 0.2s ease',
                    opacity: event.is_active ? 1 : 0.7
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'none';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {/* Event Header */}
                  <div style={{
                    padding: '20px 20px 0 20px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '15px'
                  }}>
                    <h3 style={{
                      color: '#4a5568',
                      margin: '0',
                      fontSize: '1.2em',
                      fontWeight: '600',
                      lineHeight: '1.3',
                      flex: '1'
                    }}>
                      {event.title}
                    </h3>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '0.75em',
                      fontWeight: '600',
                      whiteSpace: 'nowrap',
                      background: event.is_active ? '#c6f6d5' : '#fed7d7',
                      color: event.is_active ? '#22543d' : '#742a2a'
                    }}>
                      {event.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>

                  {/* Event Content */}
                  <div style={{ padding: '15px 20px' }}>
                    <p style={{
                      color: '#718096',
                      margin: '0 0 15px 0',
                      lineHeight: '1.5',
                      fontSize: '0.95em'
                    }}>
                      {event.description}
                    </p>
                    
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px'
                    }}>
                      <div style={{ fontSize: '0.9em', color: '#4a5568' }}>
                        <strong style={{ color: '#2d3748', marginRight: '8px' }}>Fecha:</strong>
                        {formatDate(event.event_date)}
                      </div>
                      <div style={{ fontSize: '0.9em', color: '#4a5568' }}>
                        <strong style={{ color: '#2d3748', marginRight: '8px' }}>Hora:</strong>
                        {formatTime(event.event_time)} hs.
                      </div>
                      <div style={{ fontSize: '0.9em', color: '#4a5568' }}>
                        <strong style={{ color: '#2d3748', marginRight: '8px' }}>Tipo:</strong>
                        {event.event_type}
                      </div>
                      {event.is_recurring && !event.parent_event_id && (
                        <div style={{
                          background: '#e6f3ff',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          fontSize: '0.85em',
                          color: '#2c5282',
                          border: '1px solid #4299e1',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          marginTop: '4px'
                        }}>
                          <span style={{ fontSize: '1.2em' }}>🔄</span>
                          <span>
                            <strong>Evento Recurrente</strong><br />
                            {event.recurrence_type === 'daily' && 'Diario'}
                            {event.recurrence_type === 'weekdays' && 'Días laborables'}
                            {event.recurrence_type === 'weekly' && 'Semanal'}
                            {event.recurrence_type === 'biweekly' && 'Quincenal'}
                            {event.recurrence_type === 'monthly' && 'Mensual'}
                            {event.recurrence_type === 'custom' && 'Personalizado'}
                          </span>
                        </div>
                      )}
                      {event.parent_event_id && (
                        <div style={{
                          background: '#f0f4f8',
                          padding: '6px 10px',
                          borderRadius: '6px',
                          fontSize: '0.8em',
                          color: '#4a5568',
                          marginTop: '4px'
                        }}>
                          📌 Instancia de evento recurrente
                        </div>
                      )}
                      {event.location && (
                        <div style={{ fontSize: '0.9em', color: '#4a5568' }}>
                          <strong style={{ color: '#2d3748', marginRight: '8px' }}>Ubicación:</strong>
                          {event.location}
                        </div>
                      )}
                      {event.meeting_link && (
                        <div style={{ fontSize: '0.9em', color: '#4a5568' }}>
                          <strong style={{ color: '#2d3748', marginRight: '8px' }}>Enlace:</strong>
                          <a 
                            href={event.meeting_link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{
                              color: '#667eea',
                              textDecoration: 'none',
                              marginLeft: '8px'
                            }}
                            onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                            onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                          >
                            Google Meet
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Event Actions */}
                  <div style={{
                    padding: '15px 20px 20px',
                    display: 'flex',
                    gap: '8px',
                    flexWrap: 'wrap'
                  }}>
                    {event.is_recurring && !event.parent_event_id && (
                      <button 
                        onClick={() => handleViewInstances(event)}
                        style={{
                          padding: '6px 12px',
                          background: '#e6f3ff',
                          color: '#2c5282',
                          border: '1px solid #4299e1',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.85em',
                          fontWeight: '600'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#bee3f8'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#e6f3ff'}
                      >
                        📅 Ver Instancias
                      </button>
                    )}
                    <button 
                      onClick={() => handleEdit(event)}
                      style={{
                        padding: '6px 12px',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.85em',
                        fontWeight: '500',
                        transition: 'all 0.2s ease',
                        background: '#667eea',
                        color: 'white'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#5a67d8'}
                      onMouseLeave={(e) => e.target.style.background = '#667eea'}
                    >
                      Editar
                    </button>
                    <button 
                      onClick={() => toggleEventStatus(event.id)}
                      style={{
                        padding: '6px 12px',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.85em',
                        fontWeight: '500',
                        transition: 'all 0.2s ease',
                        background: event.is_active ? '#ed8936' : '#48bb78',
                        color: 'white'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = event.is_active ? '#dd6b20' : '#38a169';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = event.is_active ? '#ed8936' : '#48bb78';
                      }}
                    >
                      {event.is_active ? 'Desactivar' : 'Activar'}
                    </button>
                    <button 
                      onClick={() => deleteEvent(event.id, event.title)}
                      style={{
                        padding: '6px 12px',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.85em',
                        fontWeight: '500',
                        transition: 'all 0.2s ease',
                        background: '#e53e3e',
                        color: 'white'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#c53030'}
                      onMouseLeave={(e) => e.target.style.background = '#e53e3e'}
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      )}

      {/* Modal para ver instancias de eventos recurrentes */}
      {viewingInstances && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            maxWidth: '900px',
            width: '100%',
            maxHeight: '85vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Header */}
            <div style={{
              padding: '24px',
              borderBottom: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h3 style={{ 
                    margin: '0 0 8px 0', 
                    color: 'white',
                    fontSize: '1.5em' 
                  }}>
                    🔄 Instancias de "{viewingInstances.title}"
                  </h3>
                  <p style={{ 
                    margin: 0, 
                    color: 'rgba(255,255,255,0.9)',
                    fontSize: '0.95em' 
                  }}>
                    {instances.length} instancias generadas
                  </p>
                </div>
                <button
                  onClick={() => {
                    setViewingInstances(null);
                    setInstances([]);
                  }}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    color: 'white',
                    fontSize: '24px',
                    cursor: 'pointer',
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Actions */}
            <div style={{
              padding: '16px 24px',
              borderBottom: '1px solid #e2e8f0',
              background: '#f8f9fa',
              display: 'flex',
              gap: '10px'
            }}>
              <button
                onClick={() => handleRegenerateInstances(viewingInstances.id)}
                style={{
                  padding: '8px 16px',
                  background: '#4299e1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.9em',
                  fontWeight: '600'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#3182ce'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#4299e1'}
              >
                ↻ Regenerar Instancias
              </button>
              <span style={{
                fontSize: '0.85em',
                color: '#718096',
                display: 'flex',
                alignItems: 'center'
              }}>
                Generará instancias para las próximas 8 semanas
              </span>
            </div>

            {/* Content */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '20px 24px'
            }}>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: '4px solid #e2e8f0',
                    borderTop: '4px solid #667eea',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 16px'
                  }}></div>
                  <p style={{ color: '#718096' }}>Cargando instancias...</p>
                </div>
              ) : instances.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <p style={{ color: '#718096', fontSize: '1.1em' }}>
                    No hay instancias generadas aún
                  </p>
                  <p style={{ color: '#a0aec0', fontSize: '0.9em', marginTop: '8px' }}>
                    Haz clic en "Regenerar Instancias" para crear nuevas
                  </p>
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gap: '12px'
                }}>
                  {instances.map((instance) => (
                    <div 
                      key={instance.id}
                      style={{
                        background: '#f8f9fa',
                        padding: '16px',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '16px'
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: '0.95em',
                          color: '#4a5568',
                          display: 'flex',
                          gap: '16px',
                          alignItems: 'center'
                        }}>
                          <span style={{ fontWeight: '600', color: '#2d3748' }}>
                            📅 {formatDate(instance.event_date)}
                          </span>
                          <span>
                            ⏰ {formatTime(instance.event_time)} hs.
                          </span>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '0.85em',
                            background: instance.is_active ? '#c6f6d5' : '#fed7d7',
                            color: instance.is_active ? '#22543d' : '#742a2a'
                          }}>
                            {instance.is_active ? 'Activo' : 'Inactivo'}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleEdit(instance)}
                        style={{
                          padding: '6px 12px',
                          background: '#667eea',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.85em',
                          fontWeight: '500'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#5568d3'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#667eea'}
                      >
                        Editar
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          @media (max-width: 768px) {
            .admin-header-content {
              flex-direction: column !important;
              gap: 15px;
              text-align: center;
            }
            .admin-controls {
              flex-direction: column !important;
              gap: 15px;
              align-items: flex-start !important;
            }
            .events-grid {
              grid-template-columns: 1fr !important;
            }
            .event-card-actions {
              flex-direction: column !important;
            }
            .event-card-actions button {
              width: 100% !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default AdminDashboard;